/**
 * Without-Abtretung Physiofit checkout screen
 *
 * Route: `/webAppOnboarding/insurance-without-abtretung-physiofit-checkout`. Consent, legal
 * docs, and checkout table for the TK variant where Physiofit submits the participation
 * confirmation with its own IBAN and the insurer pays Physiofit directly. No upfront payment
 * and no refund forwarding by the patient.
 *
 * Shared logic/presentation live in `useInsuranceCheckoutScreen` + `InsuranceCheckoutLayout`;
 * this screen only supplies the variant and its billing-detail drawer.
 *
 * Guards:
 * - Route: `OnboardingSignUpGateLayout` → `OnboardingAuthGuard` → `OnboardingCompletedGuard`.
 * - Auth: Firebase user required via `OnboardingAuthGuard` (callables use signed-in session).
 * - Onboarding state: completed patients redirected to install by `OnboardingCompletedGuard`.
 * - Screen-local: `useInsuranceExplainBillingGuard('without_abtretung_physiofit')`; `useSaveOnboardingStep('insurance_without_abtretung_physiofit_checkout')`.
 */

import { InsuranceCheckoutLayout } from '../components/content/InsuranceCheckoutLayout';
import { InsuranceWithoutAbtretungPhysiofitBillingDetailDrawer } from '../components/content/InsuranceWithoutAbtretungPhysiofitBillingDetailDrawer';
import { useInsuranceCheckoutScreen } from '../hooks/useInsuranceCheckoutScreen';
import { isPatientSubmittingInsurer } from '../insurance/metaBuilders';

export const ONBOARDING_SCREEN_META = {
  requiresFirebaseSession: true,
} as const;

export function WebOnboardingInsuranceWithoutAbtretungPhysiofitCheckoutScreen() {
  const checkout = useInsuranceCheckoutScreen('without_abtretung_physiofit');
  const { insurance, insuranceShortName, billingMath, isBillingDetailOpen, setIsBillingDetailOpen } = checkout;

  const billingDetailDrawer =
    billingMath && insuranceShortName ? (
      <InsuranceWithoutAbtretungPhysiofitBillingDetailDrawer
        open={isBillingDetailOpen}
        onClose={() => setIsBillingDetailOpen(false)}
        insuranceShortName={insuranceShortName}
        refundRate={billingMath.refundRate}
        reimbursement={billingMath.reimbursement}
        subsidy={billingMath.subsidy}
        hasBonus={Boolean(insurance?.bonus?.hasBonus)}
        patientSubmits={insurance ? isPatientSubmittingInsurer(insurance) : false}
      />
    ) : null;

  return <InsuranceCheckoutLayout checkout={checkout} billingDetailDrawer={billingDetailDrawer} />;
}
